import { api } from '../api/client';

import { API_OAUTH_URL } from '../utils';

/**
 * OAuth: how Claude on the web, ChatGPT and other remote assistants connect
 * to keepr without a key being pasted anywhere (keepr-api
 * docs/plans/agent-access.md, Decision 7; wire semantics in docs/SCHEMA.md
 * § OAuth grants).
 *
 * A grant is a key by another name. The consent page (/oauth/authorize) IS
 * the key-creation form — which collections, read or read-and-write, Can
 * change cards — and what it leaves behind carries exactly a key's scopes
 * and collection allowlist. The person sees it on the account page as a
 * CONNECTED ASSISTANT rather than a key, because that is what it is to them.
 *
 * Every route here is session-only on the server: a key or a grant can
 * never mint, list or revoke a grant. The web app always calls with the
 * session JWT, so that is invisible from here.
 */

/** Access choices, as the consent page offers them. `write` implies `read`. */
export const ACCESS_OPTIONS = [
	{ value: 'read', label: 'Read only' },
	{ value: 'write', label: 'Read and write' },
];

/** The one-line consequence beside the Can change cards switch. */
export const CARDS_CONSEQUENCE = 'Lets it create and change cards, element sets and layouts in these collections.';

/**
 * The one-line consequence beside the Can delete records switch — off by
 * default, and only live with Read and write or Can change cards (the API
 * refuses `delete` on its own). An assistant connected before 2026-09-24
 * cannot delete until it is connected again with this on.
 */
export const DELETE_CONSEQUENCE = 'Lets it delete items, attachments and saved setup in these collections — up to a daily limit. Only you can restore a deleted item, from History.';

/** The sentence every consent screen carries. */
export const ACTS_AS_YOU = 'It acts as you, in these collections only, and never as an administrator. You can disconnect it any time from Account → Connected assistants.';

/**
 * The `scope` the client asked for (space-separated: "read write cards"),
 * as the form's two controls. What the client asks for is a hint; the
 * person decides. Unknown words are ignored.
 */
export function parseScopeParam(scope) {
	const words = String(scope || '').split(/\s+/).filter(Boolean);
	return {
		access: words.includes('write') ? 'write' : 'read',
		cards: words.includes('cards'),
		// Reported for completeness; the consent page starts Can delete
		// records off whatever the client asked (see OAuthAuthorize).
		canDelete: words.includes('delete'),
	};
}

/**
 * The controls back into the scopes the API stores. `read` is always in;
 * `delete` only beside write or cards, as the API requires.
 */
export function scopesFor({ access = 'read', cards = false, canDelete = false } = {}) {
	const scopes = ['read'];
	if (access === 'write') scopes.push('write');
	if (cards) scopes.push('cards');
	if (canDelete && (access === 'write' || cards)) scopes.push('delete');
	return scopes;
}

/**
 * Who is asking, and where the code will go — before the page renders a
 * form. A refusal (unknown client, unregistered redirect) is a 400 with a
 * `code`, and the page shows it as an error: it must NEVER redirect on one.
 */
export async function describeClient({ clientId, redirectUri }) {
	const { data } = await api.get(`${API_OAUTH_URL}/client-info`, {
		params: { client_id: clientId, redirect_uri: redirectUri },
	});
	return data;
}

/** A connection's name, as long as an API key's. */
export const MAX_CONNECTION_NAME = 80;

/**
 * The person pressed Allow. 201 → { code, iss, expiresIn }. The PAGE then
 * redirects to the client with the code — the API never does.
 *
 * `replaceGrantId` is always sent: null makes a new connection, an id from
 * client-info's `existingConnections` replaces that one. (The API reads an
 * OMITTED one as a consent page from before 2026-10-04.) A blank `name`
 * keeps a replaced connection's name or takes the suggested one.
 */
export async function authorizeClient({ clientId, redirectUri, codeChallenge, codeChallengeMethod = 'S256', resource, scopes, collectionIds, name, replaceGrantId = null } = {}) {
	const payload = {
		client_id: clientId,
		redirect_uri: redirectUri,
		code_challenge: codeChallenge,
		code_challenge_method: codeChallengeMethod,
		scopes,
		replaceGrantId: replaceGrantId || null,
	};
	if (typeof name === 'string' && name.trim()) payload.name = name.trim();
	if (resource) payload.resource = resource;
	// Omitted for "all my collections" — the API refuses an empty array.
	if (Array.isArray(collectionIds) && collectionIds.length) payload.collectionIds = collectionIds;
	const { data } = await api.post(`${API_OAUTH_URL}/authorize`, payload);
	return data;
}

/**
 * The redirect back to the client: the presented redirect URI with the
 * parameters appended to whatever query it already carries. `state` and
 * `iss` go on every redirect, success and refusal alike (RFC 9207).
 */
export function buildRedirect(redirectUri, params = {}) {
	const url = new URL(redirectUri);
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, value);
	});
	return url.toString();
}

/** The caller's connected assistants, newest first — metadata only. */
export async function listGrants({ includeRevoked = false } = {}) {
	const { data } = await api.get(`${API_OAUTH_URL}/grants`, {
		params: includeRevoked ? { includeRevoked: true } : undefined,
	});
	return Array.isArray(data) ? data : [];
}

/** Rename a connection — its label only, never what it may do. → the grant as listed. */
export async function renameGrant(id, name) {
	const { data } = await api.patch(`${API_OAUTH_URL}/grants/${id}`, { name });
	return data;
}

/** Disconnect — immediate, idempotent. The assistant's next request is refused. */
export async function revokeGrant(id) {
	const { data } = await api.delete(`${API_OAUTH_URL}/grants/${id}`);
	return data;
}

/** 'read', 'read + write', either with ' + cards', and ' + delete' last. */
export function grantAccessLabel(grant) {
	const scopes = Array.isArray(grant?.scopes) ? grant.scopes : [];
	const parts = [scopes.includes('write') ? 'read + write' : 'read'];
	if (scopes.includes('cards')) parts.push('cards');
	if (scopes.includes('delete')) parts.push('delete');
	return parts.join(' + ');
}
